import { useMutation } from "@apollo/client";
import Button from "components/base/Button";
import { FileSelector, Select, Text } from "components/base/Form";
import { Alert } from "components/base/Toast";
import useFileUploader from "hooks/useFileUploader";
import { useState } from "react";
import { getOriginalFileName } from "utils/string";
import {
  HANDLE_PRODUCTION_PLAN,
  SAVE_PRODUCTION_PLAN_EXTRA_DATA,
} from "./graphql";

const PROCESSING_USERS = [
  { id: "509", name: "Livi" },
  { id: "177", name: "Yolina" },
  { id: "7", name: "Sharon" },
];
const HANDLE_ATTACHMENT_URLS_KEY = "handleAttachmentUrls";

function parseExtraData(value) {
  if (!value || typeof value !== "string") return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

const HandlePlanView = ({ plan, onCompleted }) => {
  const [handle, handleRes] = useMutation(HANDLE_PRODUCTION_PLAN, {
    onCompleted() {
      Alert("success", "Plan marked as Handled.");
      onCompleted();
    },
    onError(error) {
      Alert("error", error.message);
    },
  });
  const [savePlanExtraData, savePlanExtraDataRes] = useMutation(
    SAVE_PRODUCTION_PLAN_EXTRA_DATA,
    {
      onError(error) {
        Alert("error", error.message);
      },
    },
  );
  const [handledByUserId, setHandledByUserId] = useState(
    PROCESSING_USERS[0].id,
  );
  const [handleRemarks, setHandleRemarks] = useState("");
  const initialExtraData = parseExtraData(plan?.extraData);
  const [attachmentUrls, setAttachmentUrls] = useState(
    Array.isArray(initialExtraData[HANDLE_ATTACHMENT_URLS_KEY])
      ? initialExtraData[HANDLE_ATTACHMENT_URLS_KEY].filter(Boolean)
      : [],
  );
  const { isUploading, handleFileUpload } = useFileUploader((urls) => {
    setAttachmentUrls((prev) => [...prev, ...urls]);
  }, "qiniu");

  const removeAttachment = (indexToRemove) => {
    setAttachmentUrls((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
  };

  const submitHandler = async () => {
    if (isUploading) {
      Alert("error", "Please wait for file upload to complete.");
      return;
    }

    const nextExtraData = {
      ...initialExtraData,
      [HANDLE_ATTACHMENT_URLS_KEY]: attachmentUrls.filter(Boolean),
    };

    const saveResult = await savePlanExtraData({
      variables: {
        id: plan.id,
        extraData: JSON.stringify(nextExtraData),
      },
    }).catch(() => null);
    if (!saveResult) return;

    await handle({
      variables: {
        id: plan.id,
        handledByUserId: handledByUserId,
        handleRemarks: handleRemarks,
      },
    }).catch(() => null);
  };

  return (
    <div>
      <div className="mt-2 space-x-3">
        <label>Assign handled by:</label>
        <Select
          value={handledByUserId}
          onChange={(e) => setHandledByUserId(e.target.value)}
        >
          {PROCESSING_USERS.map((user) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </Select>
      </div>

      <div className="mt-2">
        <label>Remarks:</label>
        <Text
          className="mt-2"
          value={handleRemarks}
          onChange={(e) => setHandleRemarks(e.target.value)}
        />
        <div className="text-xs opacity-70">
          The responsible user will get an email notification about this plan is
          being handled with this remarks.
        </div>
      </div>

      <div className="mt-4 text-sm">
        {attachmentUrls.length > 0 && (
          <div>
            <label>Attachments:</label>
            <div className="space-y-2 mt-2">
              {attachmentUrls.map((url, index) => (
                <div
                  key={`${url}-${index}`}
                  className="flex items-center space-x-2 border-b border-gray-100 dark:border-gray-700 py-1"
                >
                  <Button trash onClick={() => removeAttachment(index)} />
                  <a
                    href={url}
                    className="link"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {getOriginalFileName(url)}
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-2">
          <FileSelector
            title="+ Add Files"
            uploading={isUploading}
            onChange={(e) => {
              handleFileUpload([...e.target.files]);
              e.target.value = null;
            }}
          />
        </div>
      </div>

      <hr />

      <div className="mt-2">
        <Button
          primary
          onClick={submitHandler}
          loading={handleRes.loading || savePlanExtraDataRes.loading}
        >
          Mark as Handled
        </Button>
      </div>
    </div>
  );
};

export default HandlePlanView;
